import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { ArrayHelper } from '@lib-shared/helper/class/array-helper.class';

import { LangService } from '@lib-shared/lang/services/lang.service';
import { GlobalErrorService } from '../services/global-error.service';

import { environment } from '@environments/environment';

@Injectable()
export class GlobalInterceptor implements HttpInterceptor {
  private timeout = environment.api.backend.timeout;

  private statusDefaultError = [304, 401, 500, 502, 503, 504, 599];
  private statusFallback = [[500, 599]];

  /**
   * constructor
   *
   */
  constructor(
    private globalErrorService: GlobalErrorService,
    private langService: LangService,
  ) {}

  /**
   * intercept
   *
   */
  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.handleRequest(this.addCustomHeadersToRequest(request), next);
  }

  /**
   * handleRequest
   *
   */
  private handleRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // set errorCallback
    const errorCallback = this.globalErrorService.getAndResetErrorCallback();

    // handle request
    return this.getHandleRequestWithTimeout$(request, next).pipe(
      catchError((error) => {
        // special case for status 0 (unknown, no internet connexion)
        if (error.status === 0) {
          throw error;
        }

        // special case for status 503 (force fallback)
        if (error.status === 503) {
          this.globalErrorService.httpErrorFallback();

          throw error;
        }

        // manage Fallback error
        if (
          ArrayHelper.includesAndBetween(this.statusFallback, error.status) &&
          request.headers.has('fallbackonerror')
        ) {
          this.globalErrorService.httpErrorFallback();

          throw error;
        }

        // manage basic errors
        if (
          (!errorCallback || !errorCallback(request, error)) &&
          !ArrayHelper.includesAndBetween(this.statusDefaultError, error.status)
        ) {
          this.globalErrorService.httpErrorDefault(request, error);
        }

        throw error;
      }),
    );
  }

  /**
   * addCustomHeadersToRequest
   *
   */
  private addCustomHeadersToRequest(
    request: HttpRequest<any>,
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        'Accept-Language': this.langService.getActiveLang(),
      },
    });
  }

  /**
   * getHandleRequestWithTimeout
   *
   */
  private getHandleRequestWithTimeout$(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let newRequest = request;
    let requestTimeout = this.timeout;

    // if request header has 'customTimeout'
    if (request.headers.has('customTimeout')) {
      requestTimeout = Number(request.headers.get('customTimeout'));
      newRequest = this.removeCustomTimeoutFromRequest(request);
    }

    // if request header has 'skipTimeout'
    if (request.headers.has('skipTimeout')) {
      requestTimeout = -1;
      newRequest = this.removeSkipTimeoutFromRequest(request);
    }

    return requestTimeout >= 0
      ? next.handle(newRequest).pipe(timeout(requestTimeout))
      : next.handle(newRequest);
  }

  /**
   * removeCustomTimeoutFromRequest
   *
   */
  private removeCustomTimeoutFromRequest(
    request: HttpRequest<any>,
  ): HttpRequest<any> {
    return request.clone({
      headers: request.headers.delete('customTimeout'),
    });
  }

  /**
   * removeCustomTimeoutFromRequest
   *
   */
  private removeSkipTimeoutFromRequest(
    request: HttpRequest<any>,
  ): HttpRequest<any> {
    return request.clone({
      headers: request.headers.delete('skipTimeout'),
    });
  }
}
